import type { CaseManifestV1 } from "@cold-case/shared/case-manifest";
import type { AccusationPhaseState } from "@cold-case/shared/accusation-phase";

import { accuseTabIntro } from "../content/accusation-phase-copy.js";
import {
  appendArrestChatMessage,
  closeAccusationArrestOverlay,
  isAccusationArrestOverlayOpen,
  openAccusationArrestOverlay,
  syncAccusationArrestOverlay,
  type AccusationArrestContext,
} from "./accusation-arrest-overlay.js";
import {
  bindAccusationPhaseSync,
  emitAccusationAdopt,
  emitAccusationPick,
  emitAccusationStart,
  emitAccusationVote,
  emitAccusationWarrant,
} from "./accusation-phase-sync.js";
import {
  closeAccusationVoteModal,
  isAccusationVoteModalOpen,
  openAccusationVoteModal,
  syncAccusationVoteModal,
} from "./accusation-vote-modal.js";

let lastLocalArrestChat: { text: string; at: number } | null = null;
import { closeNarrativeModal } from "../lib/narrative-modal.js";
import { setPlayerOverlayOpen } from "../lib/player-input-gate.js";
import { endFollowMode } from "./player-follow.js";
import { dismissClueToast } from "./clue-toast.js";
import { hideSessionLoading, showSessionLoading } from "./world-loading.js";
import type { Socket } from "socket.io-client";

export type AccusationControllerDeps = {
  getSessionId: () => string;
  getSocket: () => Socket | null;
  getPlayerCount: () => number;
  getUserId: () => string;
  getManifest: () => CaseManifestV1;
  getJournal: () => Record<string, unknown>;
  getPlayers: () => {
    userId: string;
    displayName: string;
    characterNpcId: string;
  }[];
  getPlayerNames: () => string[];
  zoneName: (z: "downtown" | "maple_heights") => string;
  minEvidence: number;
  closeJournal: () => void;
  closeManual: () => void;
  closeRadio: () => void;
  dismissNpcUi: () => void;
  hideOutcome: () => void;
  onAccusationPhaseChange: (active: boolean, stage: AccusationPhaseState["stage"] | null) => void;
  onCancelled: (reason: string) => void;
  onStartError: (message: string) => void;
  onVotePassed?: () => void;
};

let deps: AccusationControllerDeps | null = null;
let unbindSync: (() => void) | null = null;
let pendingStartLoader = false;
let lastStage: AccusationPhaseState["stage"] | null = null;
function proposerName(phase: AccusationPhaseState): string {
  const p = deps?.getPlayers().find((x) => x.userId === phase.proposerUserId);
  return p?.displayName ?? "A detective";
}

function prepareOverlays(): void {
  endFollowMode(true);
  deps?.dismissNpcUi();
  deps?.closeJournal();
  deps?.closeManual();
  deps?.closeRadio();
  deps?.hideOutcome();
  closeNarrativeModal();
  dismissClueToast();
}

/** Re-apply input freeze after phase sync when accusation DOM is still open. */
export function syncAccusationInputGate(phase: AccusationPhaseState): void {
  if (phase.stage === "vote" && isAccusationVoteModalOpen()) {
    setPlayerOverlayOpen("accusation-vote", true);
  } else if (phase.stage === "arrest" && isAccusationArrestOverlayOpen()) {
    setPlayerOverlayOpen("accusation-arrest", true);
  }
}

function arrestContext(): AccusationArrestContext | null {
  if (!deps) return null;
  return {
    manifest: deps.getManifest(),
    journal: deps.getJournal(),
    playerNames: deps.getPlayerNames(),
    players: deps.getPlayers(),
    selfUserId: deps.getUserId(),
    zoneName: deps.zoneName,
    minEvidence: deps.minEvidence,
  };
}

function showLoaderThen(cb: () => void): void {
  showSessionLoading("downtown");
  requestAnimationFrame(() => {
    cb();
    hideSessionLoading();
  });
}

function openVoteUi(phase: AccusationPhaseState): void {
  if (!deps) return;
  const players = deps.getPlayers().map((p) => ({
    userId: p.userId,
    displayName: p.displayName,
  }));
  if (isAccusationVoteModalOpen()) {
    syncAccusationVoteModal(phase, players);
    syncAccusationInputGate(phase);
    return;
  }
  showLoaderThen(() => {
    openAccusationVoteModal(
      phase,
      proposerName(phase),
      deps!.getPlayerCount(),
      deps!.getUserId(),
      players,
      {
        onVote: (vote) => {
          emitAccusationVote(deps!.getSocket(), deps!.getSessionId(), vote);
        },
      }
    );
  });
}

function openArrestUi(phase: AccusationPhaseState, opts?: { skipLoader?: boolean }): void {
  if (!deps) return;
  const ctx = arrestContext();
  if (!ctx) return;
  const h = {
    onPick: (killer_instance_id: string) => {
      emitAccusationPick(deps!.getSocket(), deps!.getSessionId(), killer_instance_id);
    },
    onAdopt: (fromUserId: string) => {
      emitAccusationAdopt(deps!.getSocket(), deps!.getSessionId(), fromUserId);
    },
    onWarrant: () => {
      emitAccusationWarrant(deps!.getSocket(), deps!.getSessionId());
    },
    onRadioSend: (text: string) => {
      const me = deps!.getPlayers().find((p) => p.userId === deps!.getUserId());
      const name = me?.displayName ?? "You";
      lastLocalArrestChat = { text, at: Date.now() };
      appendArrestChatMessage(name, text);
      deps!
        .getSocket()
        ?.emit("game:radio", { sessionId: deps!.getSessionId(), text });
    },
  };
  if (isAccusationArrestOverlayOpen()) {
    syncAccusationArrestOverlay(phase);
    syncAccusationInputGate(phase);
    return;
  }
  const open = () => openAccusationArrestOverlay(phase, ctx, h);
  if (opts?.skipLoader) {
    open();
    return;
  }
  showLoaderThen(open);
}

function handlePhase(phase: AccusationPhaseState | null): void {
  if (!deps) return;
  if (pendingStartLoader) {
    hideSessionLoading();
    pendingStartLoader = false;
  }
  if (!phase) {
    deps.onAccusationPhaseChange(false, null);
    lastStage = null;
    closeAccusationVoteModal();
    closeAccusationArrestOverlay();
    return;
  }
  prepareOverlays();
  if (phase.stage === "vote") {
    deps.onAccusationPhaseChange(true, "vote");
    lastStage = "vote";
    closeAccusationArrestOverlay();
    openVoteUi(phase);
    syncAccusationInputGate(phase);
    return;
  }
  const fromVote = lastStage === "vote";
  if (fromVote) {
    deps.onVotePassed?.();
  }
  deps.onAccusationPhaseChange(true, "arrest");
  closeAccusationVoteModal();
  lastStage = "arrest";
  openArrestUi(phase, { skipLoader: fromVote });
  syncAccusationInputGate(phase);
}

export function initAccusationController(d: AccusationControllerDeps): void {
  deps = d;
  lastStage = null;
}

export function bindAccusationController(socket: Socket): void {
  unbindSync?.();
  unbindSync = bindAccusationPhaseSync(socket, {
    onPhase: (phase, error) => {
      if (error) {
        hideSessionLoading();
        pendingStartLoader = false;
        deps?.onStartError(error);
        return;
      }
      handlePhase(phase);
    },
    onCancelled: (reason) => {
      hideSessionLoading();
      pendingStartLoader = false;
      lastLocalArrestChat = null;
      deps?.onAccusationPhaseChange(false, null);
      lastStage = null;
      closeAccusationVoteModal();
      closeAccusationArrestOverlay();
      deps?.onCancelled(reason);
    },
  });
}

export function teardownAccusationController(): void {
  unbindSync?.();
  unbindSync = null;
  pendingStartLoader = false;
  deps?.onAccusationPhaseChange(false, null);
  lastStage = null;
  closeAccusationVoteModal();
  closeAccusationArrestOverlay();
  deps = null;
}

export function closeAccusationOverlays(): void {
  lastLocalArrestChat = null;
  deps?.onAccusationPhaseChange(false, null);
  lastStage = null;
  closeAccusationVoteModal();
  closeAccusationArrestOverlay();
}

export function resumeAccusationPhase(phase: AccusationPhaseState | null | undefined): void {
  if (phase) {
    handlePhase(phase);
    return;
  }
  if (isAccusationPhaseActive()) {
    handlePhase(null);
  }
}

export function isAccusationPhaseActive(): boolean {
  return (
    lastStage !== null ||
    pendingStartLoader ||
    isAccusationVoteModalOpen() ||
    isAccusationArrestOverlayOpen()
  );
}

export function getAccusationStage(): AccusationPhaseState["stage"] | null {
  return lastStage;
}

export function accusationBlockedMessage(): string {
  if (lastStage === "arrest" || isAccusationArrestOverlayOpen()) {
    return "Finish the arrest phase on screen first.";
  }
  if (lastStage === "vote" || isAccusationVoteModalOpen()) {
    return "Finish the accusation vote first.";
  }
  return "An accusation is in progress.";
}

export function startAccusationFromCaseFile(): void {
  if (!deps) return;
  if (lastStage) {
    deps.onStartError("An accusation is already in progress.");
    return;
  }
  prepareOverlays();
  pendingStartLoader = true;
  showSessionLoading("downtown");
  emitAccusationStart(deps.getSocket(), deps.getSessionId());
}

function accuseGateCallout(
  clueCount: number,
  minEvidence: number,
  talkedCount: number
): string {
  const needs: string[] = [];
  if (talkedCount < 1) {
    needs.push("Interview at least <strong>one suspect</strong> in the world (case notes → Begin interview).");
  }
  if (clueCount < minEvidence) {
    const missing = minEvidence - clueCount;
    needs.push(
      `Discover <strong>${missing}</strong> more clue${missing === 1 ? "" : "s"} (you have ${clueCount}, need ${minEvidence}).`
    );
  }
  if (!needs.length) return "";
  return `<div class="acc-phase-callout" style="border-color:#5a4a2a">
<p><strong>Before you can accuse</strong></p>
<ul style="margin:0;padding-left:1.2rem">${needs.map((n) => `<li>${n}</li>`).join("")}</ul>
</div>`;
}

export function renderMultiplayerAccuseTabHtml(
  playerCount: number,
  canStart: boolean,
  clueCount: number,
  minEvidence: number,
  phaseActive: boolean,
  talkedCount = 0
): string {
  const gate = accuseGateCallout(clueCount, minEvidence, talkedCount);
  if (phaseActive) {
    return `${accuseTabIntro(playerCount)}
<p style="color:#ffb86c">An accusation vote or arrest phase is already in progress.</p>`;
  }
  if (!canStart || talkedCount < 1) {
    return `${accuseTabIntro(playerCount)}
${gate || `<p>Need at least <strong>${minEvidence}</strong> discovered clue(s) in your journal (you have ${clueCount}).</p>`}`;
  }
  return `${accuseTabIntro(playerCount)}
${gate}
<button type="button" id="btn-start-accusation" class="primary">Start an accusation</button>`;
}

export function onAccusationRadioMessage(username: string, text: string): void {
  if (!isAccusationArrestOverlayOpen()) return;
  if (
    lastLocalArrestChat &&
    lastLocalArrestChat.text === text &&
    Date.now() - lastLocalArrestChat.at < 5000
  ) {
    const me = deps?.getPlayers().find((p) => p.userId === deps?.getUserId());
    if (me && username === me.displayName) {
      lastLocalArrestChat = null;
      return;
    }
  }
  appendArrestChatMessage(username, text);
}
